import { EntityConfig } from '../types/toast.types';

/**
 * ENTITY REGISTRY
 *
 * Central configuration for all entities in the O2 system.
 * Each entity MUST define all required fields to ensure type safety.
 *
 * How to add a new entity:
 * 1. Add a new key with kebab-case matching your API endpoint
 * 2. Fill in ALL required fields (name, article, messages.success)
 * 3. Optionally add custom error messages in messages.error
 *
 * TypeScript will enforce all required fields at compile time.
 */
export const ENTITY_REGISTRY = {
  // ============================================================================
  // O2-SALES ENTITIES
  // ============================================================================

  'price-lists': {
    name: {
      singular: 'listino',
      plural: 'listini',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Listino creato con successo',
        updated: 'Listino aggiornato con successo',
        deleted: 'Listino eliminato con successo',
      },
      error: {
        notFound: 'Il listino non è stato trovato',
        conflict: 'Un listino con questo nome o codice è già presente',
        validation: 'I dati del listino non sono validi',
      },
    },
  },

  complexities: {
    name: {
      singular: 'complessità',
      plural: 'complessità',
    },
    article: {
      singular: 'la',
      plural: 'le',
    },
    messages: {
      success: {
        created: 'Complessità creata con successo',
        updated: 'Complessità aggiornata con successo',
        deleted: 'Complessità eliminata con successo',
      },
      error: {
        notFound: 'La complessità non è stata trovata',
        conflict: 'Una complessità con questo codice è già presente nel listino',
        validation: 'I dati della complessità non sono validi',
      },
    },
  },

  'price-list-categories': {
    name: {
      singular: 'categoria',
      plural: 'categorie',
    },
    article: {
      singular: 'la',
      plural: 'le',
    },
    messages: {
      success: {
        created: 'Categoria creata con successo',
        updated: 'Categoria aggiornata con successo',
        deleted: 'Categoria eliminata con successo',
      },
      error: {
        notFound: 'La categoria non è stata trovata',
        conflict: 'Una categoria con questo nome o codice è già presente',
        validation: 'I dati della categoria non sono validi',
      },
    },
  },

  contacts: {
    name: {
      singular: 'contatto',
      plural: 'contatti',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Contatto creato con successo',
        updated: 'Contatto aggiornato con successo',
        deleted: 'Contatto eliminato con successo',
      },
      error: {
        notFound: 'Il contatto non è stato trovato',
        conflict: 'Un contatto con questa email è già presente',
        validation: 'I dati del contatto non sono validi',
      },
    },
    translations: {
      'Cannot delete contact': 'Impossibile eliminare il contatto: ha elementi associati',
    },
  },

  contracts: {
    name: {
      singular: 'contratto',
      plural: 'contratti',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Contratto creato con successo',
        updated: 'Contratto aggiornato con successo',
        deleted: 'Contratto eliminato con successo',
      },
      error: {
        notFound: 'Il contratto non è stato trovato',
        conflict: 'Un contratto con questo numero è già presente',
        validation: 'I dati del contratto non sono validi',
      },
    },
    // Prefix-matched against the BE exception messages by the toast
    // interceptor: when a backend message starts with one of these
    // keys, the dynamic suffix (id / code) is discarded and the
    // user sees a clean Italian sentence. The original technical
    // detail is still in the network response for debugging.
    translations: {
      'A contract already exists for quote':
        'Esiste già un contratto per il preventivo selezionato',
      'Contract with code':
        'Esiste già un contratto con lo stesso codice in questo tenant',
      'Quote with ID':
        'Il preventivo selezionato non è stato trovato',
      'Contract with ID':
        'Il contratto non è stato trovato',
      'Source VACATION quote is missing a positive rate':
        'Il preventivo a vacazione non ha tariffe valide su tutti i livelli: impossibile creare il contratto',
      'billableHourlyRate is required when signing a contract':
        "Manca la tariffa oraria fatturabile: necessaria per firmare il contratto",
      'VACATION contract cannot be signed':
        'Contratto a vacazione: completa tutti i campi richiesti prima di firmare',
      'levelRatesSnapshot is immutable':
        'Le tariffe per livello non sono più modificabili dopo la firma del contratto',
      'Contract can only be deleted when status is DRAFT':
        'Il contratto può essere eliminato solo se in stato Bozza',
      'BILLING_START_DATE_LOCKED_AFTER_FIRST_PAYMENT':
        'La data di inizio fatturazione non è più modificabile dopo la prima scadenza generata',
    },
  },

  quotes: {
    name: {
      singular: 'preventivo',
      plural: 'preventivi',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Preventivo creato con successo',
        updated: 'Preventivo aggiornato con successo',
        deleted: 'Preventivo eliminato con successo',
      },
      error: {
        notFound: 'Il preventivo non è stato trovato',
        conflict: 'Un preventivo con questo numero è già presente',
        validation: 'I dati del preventivo non sono validi',
      },
    },
    translations: {
      'Quote with code': 'Esiste già un preventivo con lo stesso codice',
      'Quote not found': 'Il preventivo non è stato trovato',
      'Vacation item with object':
        'Esiste già una vacazione per questo oggetto',
      'Vacation item not found': 'La vacazione non è stata trovata',
    },
  },

  // ============================================================================
  // O2-CORE ENTITIES
  // ============================================================================

  projects: {
    name: {
      singular: 'progetto',
      plural: 'progetti',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Progetto creato con successo',
        updated: 'Progetto aggiornato con successo',
        deleted: 'Progetto eliminato con successo',
      },
      error: {
        notFound: 'Il progetto non è stato trovato',
        conflict: 'Un progetto con questo codice è già presente',
        validation: 'I dati del progetto non sono validi',
      },
    },
    translations: {
      'Project with code': 'Progetto con questo codice già esistente',
      'Cannot complete project': 'Impossibile completare il progetto: ci sono task non ancora completati',
      'Project not found': 'Il progetto non è stato trovato',
      'Project is not a vacation':
        "Operazione disponibile solo per i progetti a vacazione (T&M)",
      'BILLABLE_FLAG_NOT_ALLOWED_FOR_NON_VACATION':
        'Il flag "fatturabile" è disponibile solo per i progetti a vacazione',
      'USER_LEVEL_REQUIRED_FOR_VACATION':
        "Per loggare ore su un progetto a vacazione l'utente deve avere un livello (Junior / Mid / Senior)",
      'TIMESHEET_IDS_NOT_OWNED_BY_PROJECT':
        'Alcune righe selezionate non appartengono a questo progetto',
      'PROJECT_NOT_OWNED_BY_CALLER':
        'Non hai i permessi per agire su questo progetto',
      'PROJECT_MISSING_CONTRACT':
        "Il progetto non risulta collegato a un contratto: contatta un amministratore",
    },
  },

  'project-tasks': {
    name: {
      singular: 'attività',
      plural: 'attività',
    },
    article: {
      singular: "l'",
      plural: 'le',
    },
    messages: {
      success: {
        created: 'Attività creata con successo',
        updated: 'Attività aggiornata con successo',
        deleted: 'Attività eliminata con successo',
      },
      error: {
        notFound: "L'attività non è stata trovata",
        conflict: "Un'attività con questo codice è già presente",
        validation: "I dati dell'attività non sono validi",
      },
    },
    translations: {
      'Cannot modify sprint on a completed task': 'Non è possibile modificare la pianificazione di un task completato',
      'Cannot shorten sprint': 'Non è possibile accorciare la pianificazione: ci sono subtask assegnati nel periodo rimosso',
      'Cannot modify a completed task': 'Non è possibile modificare un task completato. Solo le date di pianificazione possono essere cambiate.',
    },
  },

  'project-subtasks': {
    name: {
      singular: 'subtask',
      plural: 'subtask',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Subtask creato con successo',
        updated: 'Subtask aggiornato con successo',
        deleted: 'Subtask eliminato con successo',
      },
      error: {
        notFound: 'Il subtask non è stato trovato',
        validation: 'I dati del subtask non sono validi',
      },
    },
    translations: {
      'Cannot modify a completed subtask': 'Non è possibile modificare un subtask completato',
      'Cannot delete a completed subtask': 'Non è possibile eliminare un subtask completato',
      'Cannot create subtask on a completed task': 'Non è possibile creare un subtask su un task completato',
    },
  },

  'standard-tasks': {
    name: {
      singular: 'task standard',
      plural: 'task standard',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Task standard creato con successo',
        updated: 'Task standard aggiornato con successo',
        deleted: 'Task standard eliminato con successo',
      },
      error: {
        notFound: 'Il task standard non è stato trovato',
        conflict: 'Un task standard con questo codice è già presente',
        validation: 'I dati del task standard non sono validi',
      },
    },
  },

  'standard-subtasks': {
    name: {
      singular: 'subtask standard',
      plural: 'subtask standard',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Subtask standard creato con successo',
        updated: 'Subtask standard aggiornato con successo',
        deleted: 'Subtask standard eliminato con successo',
      },
      error: {
        notFound: 'Il subtask standard non è stato trovato',
        conflict: 'Un subtask standard con questo codice è già presente',
        validation: 'I dati del subtask standard non sono validi',
      },
    },
  },

  timesheets: {
    name: {
      singular: 'timesheet',
      plural: 'timesheet',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Timesheet creato con successo',
        updated: 'Timesheet aggiornato con successo',
        deleted: 'Timesheet eliminato con successo',
      },
      error: {
        notFound: 'Il timesheet non è stato trovato',
        validation: 'I dati del timesheet non sono validi',
      },
    },
    // Backend error codes thrown by the timesheets service that
    // deserve a tailored Italian copy instead of the raw English
    // identifier. Without these the toast would show the literal
    // string (e.g. "PERIOD_ALREADY_CLOSED"), unfriendly for the PM.
    translations: {
      PERIOD_ALREADY_CLOSED:
        'Questo rapportino è in un periodo di fatturazione già chiuso e non può essere modificato.',
      USER_LEVEL_REQUIRED_FOR_VACATION:
        'Questo utente non ha un livello di seniority impostato. Imposta il livello dal profilo prima di registrare ore su un progetto a vacazione.',
    },
  },

  'project-users': {
    name: {
      singular: 'assegnazione utente',
      plural: 'assegnazioni utente',
    },
    article: {
      singular: "l'",
      plural: 'le',
    },
    messages: {
      success: {
        created: 'Utente assegnato al progetto con successo',
        updated: 'Assegnazione aggiornata con successo',
        deleted: 'Utente rimosso dal progetto con successo',
      },
      error: {
        notFound: "L'assegnazione non è stata trovata",
        conflict: "L'utente è già assegnato a questo progetto",
        validation: "I dati dell'assegnazione non sono validi",
      },
    },
  },

  // ============================================================================
  // O2-USERS ENTITIES
  // ============================================================================

  users: {
    name: {
      singular: 'utente',
      plural: 'utenti',
    },
    article: {
      singular: "l'",
      plural: 'gli',
    },
    messages: {
      success: {
        created: 'Utente creato con successo',
        updated: 'Utente aggiornato con successo',
        deleted: 'Utente eliminato con successo',
      },
      error: {
        notFound: "L'utente non è stato trovato",
        conflict: "Un utente con questa email è già presente",
        validation: "I dati dell'utente non sono validi",
        forbidden: "Non hai i permessi per modificare questo utente",
      },
    },
  },

  tenants: {
    name: {
      singular: 'tenant',
      plural: 'tenant',
    },
    article: {
      singular: 'il',
      plural: 'i',
    },
    messages: {
      success: {
        created: 'Tenant creato con successo',
        updated: 'Tenant aggiornato con successo',
        deleted: 'Tenant eliminato con successo',
      },
      error: {
        notFound: 'Il tenant non è stato trovato',
        conflict: 'Un tenant con questo nome è già presente',
        validation: 'I dati del tenant non sono validi',
        forbidden: 'Non hai i permessi per modificare questo tenant',
      },
    },
  },

  // ============================================================================
  // Add more entities here as needed
  // ============================================================================

} as const satisfies Record<string, EntityConfig>;

/**
 * Type-safe entity key derived from the registry
 */
export type EntityKey = keyof typeof ENTITY_REGISTRY;

/**
 * Helper to get entity config with full type safety
 */
export function getEntityConfig(key: EntityKey): EntityConfig {
  return ENTITY_REGISTRY[key];
}
